/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import React from 'react';
import {
  arrayOf,
  func,
  string,
  oneOfType,
  node,
  number,
} from 'prop-types';
import { addEventListener } from 'consolidated-events';
import scrollIntoView from 'scroll-into-view';
import { startAnimation } from 'components/common/helpers/animation';
import TabPanel from './TabPanel';
import Small from './Small';
import './AnchorTabs.scss';

class AnchorTabs extends React.Component {
  constructor() {
    super();
    this.state = {
      selectedIndex: 0,
      tabWidth: 0,
      tabHeight: 0,
      tabPanels: null,
      tabIndex: {},
      shouldAutoScroll: true,
      isAnchored: false,
    };

    this.isScrolling = null;
    this.tab = React.createRef();
    this.tabPlaceholder = React.createRef();
    this.tabs = React.createRef();
  }

  componentDidMount() {
    this.removeScrollListener = addEventListener(
      window,
      'scroll',
      this.shouldAnchorTabs,
    );
    this.removeResizeListener = addEventListener(
      window,
      'resize',
      this.shouldAnchorTabs,
    );

    if (this.tab?.current) {
      this.setState({ tabHeight: this.tab.current.clientHeight });
    }
  }

  componentWillUnmount() {
    this.removeScrollListener();
    this.removeResizeListener();
  }

  static getDerivedStateFromProps({ selectedIndex }) {
    if (parseInt(selectedIndex, 10)) {
      return { selectedIndex };
    }
    return null;
  }

  onTabClickWithIndex = (i) => {
    const { onChange } = this.props;
    const { shouldAutoScroll } = this.state; // prevent auto scrolling on click event
    if (shouldAutoScroll) {
      this.setState({
        selectedIndex: i,
        tabWidth: this.tab.current.offsetWidth,
        shouldAutoScroll: false,
      });

      // Get the selected panel ref and scroll into view
      const { tabPanels } = this.state;
      const element = tabPanels[i].current;
      startAnimation(() => {
        scrollIntoView(element, {
          time: 600,
          align: {
            top: 0,
          },
        }, () => {
          this.setState({
            shouldAutoScroll: true, // enable scrolling after animation
          });
        });
      });

      if (onChange) onChange(i);
    }
  }

  // Get the tab panel refs for all panels that have prop isTabPanel only once
  getTabPanelRefs = () => {
    const { tabPanels } = this.state;
    const { children } = this.props;
    if (!tabPanels && children[1]) {
      const panels = {};
      const tabIndex = {};

      let count = 0;
      children.map((childTab, i) => {
        if (childTab && childTab?.props?.isTabPanel) {
          panels[count] = React.createRef();
          tabIndex[i] = count; // the tabIndex is mapping between the child index and the tab index
          count++;
        }
        return childTab;
      });
      this.setState({
        tabPanels: panels,
        tabIndex,
      });
    }

    return tabPanels;
  };

  // Listen to scroll and resize events for anchoring tabs to top and auto scrolling
  shouldAnchorTabs = () => {
    if (this.tab?.current && this.tabPlaceholder?.current) {
      const { isAnchored: prevIsAnchored } = this.state;
      const isAnchored = prevIsAnchored
        ? this.tabPlaceholder.current.getBoundingClientRect().top < 0
        : this.tab.current.getBoundingClientRect().top < 0;

      if (prevIsAnchored !== isAnchored) {
        this.setState({
          isAnchored,
        });
      }

      this.setState({
        tabWidth: this.tab.current.offsetWidth,
      });
    }

    const {
      tabPanels,
      selectedIndex,
      shouldAutoScroll,
    } = this.state;

    // shouldAutoScroll is used to prevent scrolling when a user clicks a tab button
    if (shouldAutoScroll) {
      window.clearTimeout(this.isScrolling);
      this.isScrolling = setTimeout(() => {
        let currentIndex = selectedIndex;
        const tops = [];
        if (tabPanels) {
          // Iterate to get panel distance to top
          Object.values(tabPanels).forEach((panel) => {
            tops.push(panel.current.getBoundingClientRect().bottom - 100);
          });

          // Find the panel index which is greater then zero and closest to zero
          tops.reduce((pre, cur, i) => {
            if (cur > 0 && cur < pre) {
              currentIndex = i;
              return cur;
            }
            return pre;
          }, 999999);

          // changing the selected index moves active cursor to the next index
          this.setState({
            selectedIndex: currentIndex,
          });
        }
      });
    }
  };

  render() {
    const {
      selectedIndex,
      tabWidth,
      tabHeight,
      isAnchored,
      tabIndex,
    } = this.state;
    const {
      tabs,
      children,
    } = this.props;
    const tabPanelRefs = this.getTabPanelRefs();
    return (
      <div className="anchor-tabs" ref={this.tabs}>
        <div className="tab-header-wrapper">
          <div className={`border-wrapper ${isAnchored ? 'anchor' : ''}`}>
            <div className="container-wrapper">
              <div className="row-wrapper">
                <div className="col-wrapper">
                  {tabs.map((tab, i) => (
                    <div
                      // eslint-disable-next-line
                      key={i}
                      className="anchor-tab"
                      style={{
                        minWidth: `${1 / tabs.length * 100}%`,
                        flexBasis: `${1 / tabs.length * 100}%`,
                      }}
                      onClick={() => this.onTabClickWithIndex(i)}
                      ref={this.tab}
                    >
                      <Small>
                        {(selectedIndex === i)
                          ? <strong className="anchor-tab-active">{tab}</strong>

                          : <span className="anchor-tab-inactive">{tab}</span>
                        }
                      </Small>
                      {i === 0 && (
                        <div
                          className="anchor-tab-active-cursor"
                          style={
                            { transform: `translateX(${selectedIndex * (tabWidth - 1)}px)` }
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div
            ref={this.tabPlaceholder}
            style={{
              display: isAnchored ? 'block' : 'none',
              height: tabHeight,
            }}
          />
        </div>
        <div className="scroller">
          {
            children.map((childTab, i) => (childTab && childTab?.props?.isTabPanel && tabPanelRefs
              ? (
                // eslint-disable-next-line
                <TabPanel key={i} ref={tabPanelRefs[tabIndex[i]]}>
                  {childTab}
                </TabPanel>
              ) : (
                // eslint-disable-next-line
                <div key={i} className="not-a-tab">
                  {childTab}
                </div>
              )))}
        </div>
      </div>
    );
  }
}

AnchorTabs.defaultProps = {
  tabs: [],
  onChange: undefined,
  children: [],
  selectedIndex: 0,
};

AnchorTabs.propTypes = {
  tabs: arrayOf(string),
  onChange: func,
  children: oneOfType([
    arrayOf(node),
    node,
  ]),
  selectedIndex: number,
};

export default AnchorTabs;
